import { wireIntegrationLocate } from '@/lib/notifications/bind-integration-locate.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { ticketApi } from '@/lib/services/ticket.api.js';

import { ASSET_LIST_DEFAULT_SORT, useAssetsStore } from '@/store/assets.store.js';
import { useProcessStore } from '@/store/process.store.js';
import { useRiskRegisterStore } from '@/store/risk-register.store.js';
import { useControlStore } from '@/store/control.store.js';
import { useDocumentStore } from '@/store/document.store.js';
import { useIncidentStore } from '@/store/incident.store.js';
import { useBcpStore } from '@/store/bcp.store.js';
import { useBiaStore } from '@/store/bia.store.js';
import { useCrisisEventStore } from '@/store/crisis-event.store.js';
import { useVendorsStore } from '@/store/vendors.store.js';
import { useMeetingStore } from '@/store/meeting.store.js';
import { useExercisesStore } from '@/store/exercises.store.js';
import { useExerciseImprovementActionsStore } from '@/store/exercise-improvement-actions.store.js';
import { useCrisisRecoveryActivitiesStore } from '@/store/crisis-recovery-activities.store.js';
import { useCrisisImprovementActionsStore } from '@/store/crisis-improvement-actions.store.js';
import { useRiskTreatmentStore } from '@/store/risk-treatment.store.js';
import { useApplicationTrackingStore } from '@/store/application-tracking.store.js';
import { useCookieInventoryStore } from '@/store/cookie-inventory.store.js';
import { useDpaStore } from '@/store/dpa.store.js';
import { useDisposalStore } from '@/store/disposal.store.js';
import { useDpiaAssessmentStore } from '@/store/dpia-assessment.store.js';
import { useExplicitConsentStore } from '@/store/explicit-consent.store.js';
import { useConsentRecordStore } from '@/store/consent-record.store.js';
import { usePrivacyNoticeStore } from '@/store/privacy-notice.store.js';
import { useDataBreachStore } from '@/store/data-breach.store.js';
import { useDataSubjectRequestStore } from '@/store/data-subject-request.store.js';
import { useCommunicationStore } from '@/store/communication.store.js';
import { useContextStore } from '@/store/context.store.js';
import { useInterestedPartyStore } from '@/store/interested-party.store.js';
import { useScopeDocumentStore } from '@/store/scope-document.store.js';
import { useAssessmentStore } from '@/store/assessment.store.js';
import { useCompetenceStore } from '@/store/competence.store.js';
import { useObjectiveStore } from '@/store/objective.store.js';
import { useVulnerabilityStore } from '@/store/vulnerability.store.js';
import { useTrainingStore } from '@/store/training.store.js';
import { useChangeRequestStore } from '@/store/change-request.store.js';
import { useProblemStore } from '@/store/problem.store.js';
import { useServiceCatalogItemStore } from '@/store/service-catalog-item.store.js';
import { useUserStore } from '@/store/user.store.js';
import { useRoleStore } from '@/store/role.store.js';
import { useDelegationStore } from '@/store/delegation.store.js';
import { useAssetGroupStore } from '@/store/asset-group.store.js';
import { useAssetCategoryStore } from '@/store/asset-category.store.js';
import { usePdInventoryStore } from '@/store/pd-inventory.store.js';
import { useWorkflowTemplateStore } from '@/store/workflow-template.store.js';
import { usePartyTemplateStore } from '@/store/party-template.store.js';
import { useWebhookSubscriptionStore } from '@/store/webhook-subscription.store.js';
import { useTicketStore } from '@/store/ticket.store.js';
import { useCorrectiveActionStore } from '@/store/corrective-action.store.js';
import { useAuditEvaluationStore } from '@/store/audit-evaluation.store.js';
import { useAssignmentTemplateStore } from '@/store/assignment-template.store.js';
import { useWeaknessStore } from '@/store/weakness.store.js';
import { useSystemSurveysStore } from '@/store/system-forms.store.js';
import { useIntegrationConnectionStore } from '@/store/integration-connection.store.js';
import { useBigdesRoadMapWorkPackageStore } from '@/store/bigdes-road-map-work-package.store.js';
import { useBigdesCompensatoryControlStore } from '@/store/bigdes-compensatory-control.store.js';
import { assignmentTemplateApi } from '@/lib/services/assignment-template-lookup.api.js';

const tenantId = () => useTenantStore.getState().selectedTenantId;

const DEFAULT_CREATED_SORT = { sortBy: 'createdAt', sortOrder: 'desc' };

/**
 * Wraps integration fetch for createListLocate (forwards page, sort, search, extraQuery).
 */
function descList(fetchFn, { fallbackSort = DEFAULT_CREATED_SORT } = {}) {
     
    return async (page, pageSize, search = '', sortBy = '', sortOrder = 'desc', _columnFilters = {}, extraQuery = {}) => {
        const resolvedSortBy = sortBy || fallbackSort.sortBy;
        const resolvedSortOrder = sortOrder || fallbackSort.sortOrder;
        const res = await fetchFn(
            page,
            pageSize,
            search ?? '',
            resolvedSortBy,
            resolvedSortOrder,
            extraQuery ?? {},
        );
        return res?.success === false ? res : { success: true, ...res };
    };
}

/**
 * Attach locateRowInList to main list integrations (idempotent).
 * Simple table integrations (createSimpleTableIntegration) already have locateRowInList.
 */
export function wireListNotificationLocate(integrations) {
    const {
        assetsIntegration,
        riskRegisterIntegration,
        controlIntegration,
        documentIntegration,
        incidentIntegration,
        bcpIntegration,
        biaIntegration,
        crisisEventIntegration,
        vendorIntegration,
        meetingIntegration,
        exercisesIntegration,
        exerciseImprovementActionsIntegration,
        crisisRecoveryActivitiesIntegration,
        crisisImprovementActionsIntegration,
        riskTreatmentIntegration,
        applicationTrackingIntegration,
        explicitConsentIntegration,
        communicationIntegration,
        contextIntegration,
        interestedPartyIntegration,
        scopeDocumentIntegration,
        assessmentIntegration,
        assessmentQuestionIntegration,
        competenceIntegration,
        objectiveIntegration,
        vulnerabilityIntegration,
        trainingIntegration,
        changeRequestIntegration,
        problemIntegration,
        serviceCatalogItemIntegration,
        userIntegration,
        roleIntegration,
        delegationIntegration,
        assetGroupIntegration,
        assetCategoryIntegration,
        pdInventoryIntegration,
        workflowTemplateIntegration,
        partyTemplateIntegration,
        webhookSubscriptionIntegration,
        ticketIntegration,
        correctiveActionIntegration,
        auditEvaluationIntegration,
        assignmentTemplateIntegration,
        processIntegration,
        weaknessIntegration,
        formTemplateIntegration,
        cookieInventoryIntegration,
        dpaIntegration,
        disposalIntegration,
        dpiaAssessmentIntegration,
        consentRecordIntegration,
        privacyNoticeIntegration,
        dataBreachIntegration,
        dataSubjectRequestIntegration,
        integrationConnectionIntegration,
        bigdesRoadMapWorkPackageIntegration,
        bigdesCompensatoryControlIntegration,
    } = integrations;

    wireIntegrationLocate({
        integration: integrationConnectionIntegration,
        useStore: useIntegrationConnectionStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            integrationConnectionIntegration?.fetchAll({ page, pageSize, search, sortBy, sortOrder, ...extraQuery }),
        ),
    });

    wireIntegrationLocate({
        integration: bigdesRoadMapWorkPackageIntegration,
        useStore: useBigdesRoadMapWorkPackageStore,
        getItems: (s) => s.items,
        defaultSortBy: 'packageNo',
        defaultSortOrder: 'asc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            bigdesRoadMapWorkPackageIntegration?.fetchAll(page, pageSize, search, sortBy, sortOrder, extraQuery),
        ),
    });

    wireIntegrationLocate({
        integration: bigdesCompensatoryControlIntegration,
        useStore: useBigdesCompensatoryControlStore,
        getItems: (s) => s.items,
        defaultSortBy: 'controlNo',
        defaultSortOrder: 'asc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            bigdesCompensatoryControlIntegration?.fetchAll(page, pageSize, search, sortBy, sortOrder, extraQuery),
        ),
    });

    wireIntegrationLocate({
        integration: processIntegration,
        useStore: useProcessStore,
        getItems: (s) => s.processes || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            processIntegration?.fetchProcesses(page, pageSize, search, sortBy, sortOrder, extraQuery),
        ),
    });

    wireIntegrationLocate({
        integration: assetsIntegration,
        useStore: useAssetsStore,
        getItems: (s) => s.items,
        defaultSortBy: ASSET_LIST_DEFAULT_SORT.sortBy,
        defaultSortOrder: ASSET_LIST_DEFAULT_SORT.sortDirection,
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            assetsIntegration?.fetchAssets({
                page,
                pageSize,
                search,
                sortBy,
                sortDirection: sortOrder,
                columnFilters: {},
                ...extraQuery,
            }),
        { fallbackSort: { sortBy: ASSET_LIST_DEFAULT_SORT.sortBy, sortOrder: ASSET_LIST_DEFAULT_SORT.sortDirection } }),
    });

    wireIntegrationLocate({
        integration: riskRegisterIntegration,
        useStore: useRiskRegisterStore,
        getItems: (s) => s.data || s.riskRegisters,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            riskRegisterIntegration?.fetchRiskRegisters({
                page,
                pageSize,
                search,
                sortBy,
                sortDirection: sortOrder,
                tenantId: tenantId(),
                ...extraQuery,
            }),
        ),
    });

    wireIntegrationLocate({
        integration: controlIntegration,
        useStore: useControlStore,
        getItems: (s) => s.controls || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            controlIntegration?.fetchControls({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId: tenantId(),
                ...extraQuery,
            }),
        ),
    });

    wireIntegrationLocate({
        integration: documentIntegration,
        useStore: useDocumentStore,
        getItems: (s) => s.documents || s.items,
        getRowId: (row) => String(row?.ID ?? row?._id ?? row?.id ?? ''),
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: async (page, pageSize, search = '', sortBy = '', sortOrder = 'desc', _cf = {}, extraQuery = {}) => {
            const sb = sortBy || 'createdAt';
            const so = sortOrder || 'desc';
            if (typeof documentIntegration?.fetchReportDocuments === 'function') {
                await documentIntegration.fetchReportDocuments(page, pageSize, search, sb, so, {});
                return { success: true };
            }
            return descList((p, ps, s, sb2, so2, eq) =>
                documentIntegration?.fetchAll({
                    page: p,
                    pageSize: ps,
                    search: s,
                    sortBy: sb2,
                    sortOrder: so2,
                    tenantId: tenantId(),
                    ...eq,
                }),
            )(page, pageSize, search, sb, so, _cf, extraQuery);
        },
    });

    wireIntegrationLocate({
        integration: incidentIntegration,
        useStore: useIncidentStore,
        getItems: (s) => s.incidents || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                incidentIntegration?.fetchAll({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: bcpIntegration,
        useStore: useBcpStore,
        getItems: (s) => s.bcps || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                bcpIntegration?.fetchBcps({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: biaIntegration,
        useStore: useBiaStore,
        getItems: (s) => s.bias || s.data || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                biaIntegration?.fetchBias({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: crisisEventIntegration,
        useStore: useCrisisEventStore,
        getItems: (s) => s.crises || s.items,
        defaultSortBy: 'title',
        defaultSortOrder: 'asc',
        fetchList: async (page, pageSize, search = '', sortBy = '', sortOrder = 'asc') => {
            const sb = sortBy || 'title';
            const so = sortOrder || 'asc';
            await crisisEventIntegration?.fetchCrisisEvents(
                page,
                pageSize,
                search,
                sb,
                so,
                { populate: 'incidentCommander' },
            );
            return { success: true };
        },
    });

    wireIntegrationLocate({
        integration: vendorIntegration,
        useStore: useVendorsStore,
        getItems: (s) => s.vendors || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                vendorIntegration?.fetchVendors(page, pageSize, search, sortBy, sortOrder, extraQuery),
            ),
    });

    wireIntegrationLocate({
        integration: meetingIntegration,
        useStore: useMeetingStore,
        getItems: (s) => s.meetings || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                meetingIntegration?.fetchMeetings?.({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: exercisesIntegration,
        useStore: useExercisesStore,
        getItems: (s) => s.exercises,
        defaultSortBy: 'name',
        defaultSortOrder: 'asc',
         
        fetchList: async (page, pageSize, search = '', sortBy = '', sortOrder = 'asc', _cf = {}, extraQuery = {}) => {
            const sb = sortBy || 'name';
            const so = sortOrder || 'asc';
            const eq = { populate: 'facilitator,participatingVendors', ...(extraQuery || {}) };
            await exercisesIntegration?.fetchExercises(page, pageSize, search, sb, so, eq);
            return { success: true };
        },
    });

    wireIntegrationLocate({
        integration: exerciseImprovementActionsIntegration,
        useStore: useExerciseImprovementActionsStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: async (page, pageSize, search = '', sortBy = '', sortOrder = 'desc') => {
            const sb = sortBy || 'createdAt';
            const so = sortOrder || 'desc';
            await exerciseImprovementActionsIntegration?.fetchItems(
                page,
                pageSize,
                search,
                sb,
                so,
            );
            return { success: true };
        },
    });

    wireIntegrationLocate({
        integration: crisisRecoveryActivitiesIntegration,
        useStore: useCrisisRecoveryActivitiesStore,
        getItems: (s) => s.items,
        defaultSortBy: 'name',
        defaultSortOrder: 'asc',
        fetchList: async (page, pageSize, search = '', sortBy = '', sortOrder = 'asc') => {
            const sb = sortBy || 'name';
            const so = sortOrder || 'asc';
            await crisisRecoveryActivitiesIntegration?.fetchItems(
                page,
                pageSize,
                search,
                sb,
                so,
                { populate: 'owner,team,vendor' },
            );
            return { success: true };
        },
    });

    wireIntegrationLocate({
        integration: crisisImprovementActionsIntegration,
        useStore: useCrisisImprovementActionsStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: async (page, pageSize, search = '', sortBy = '', sortOrder = 'desc') => {
            const sb = sortBy || 'createdAt';
            const so = sortOrder || 'desc';
            await crisisImprovementActionsIntegration?.fetchItems(
                page,
                pageSize,
                search,
                sb,
                so,
            );
            return { success: true };
        },
    });

    wireIntegrationLocate({
        integration: riskTreatmentIntegration,
        useStore: useRiskTreatmentStore,
        getItems: (s) => s.data,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                riskTreatmentIntegration?.fetchRiskTreatments({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortDirection: sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: applicationTrackingIntegration,
        useStore: useApplicationTrackingStore,
        getItems: (s) => s.applicationTrackings,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                applicationTrackingIntegration?.fetchApplicationTrackings({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: explicitConsentIntegration,
        useStore: useExplicitConsentStore,
        getItems: (s) => s.explicitConsents || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                explicitConsentIntegration?.fetchExplicitConsents(
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    extraQuery,
                ),
            ),
    });

    wireIntegrationLocate({
        integration: cookieInventoryIntegration,
        useStore: useCookieInventoryStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                cookieInventoryIntegration?.fetchItems({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: dpaIntegration,
        useStore: useDpaStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                dpaIntegration?.fetchItems({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: disposalIntegration,
        useStore: useDisposalStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                disposalIntegration?.fetchItems({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: consentRecordIntegration,
        useStore: useConsentRecordStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                consentRecordIntegration?.fetchItems({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: communicationIntegration,
        useStore: useCommunicationStore,
        getItems: (s) => s.communications || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                communicationIntegration?.fetchCommunications(
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    extraQuery,
                ),
            ),
    });

    wireIntegrationLocate({
        integration: contextIntegration,
        useStore: useContextStore,
        getItems: (s) => s.contexts || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                contextIntegration?.fetchContexts(page, pageSize, search, sortBy, sortOrder, extraQuery),
            ),
    });

    wireIntegrationLocate({
        integration: interestedPartyIntegration,
        useStore: useInterestedPartyStore,
        getItems: (s) => s.interestedParties,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: async (page, pageSize, search = '', sortBy = '', sortOrder = 'desc') => {
            const sb = sortBy || 'createdAt';
            const so = sortOrder || 'desc';
            await interestedPartyIntegration?.fetchAll(page, pageSize, search, sb, so, {});
            return { success: true };
        },
    });

    wireIntegrationLocate({
        integration: scopeDocumentIntegration,
        useStore: useScopeDocumentStore,
        getItems: (s) => s.scopeDocuments,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: async (page, pageSize, search = '', sortBy = '', sortOrder = 'desc') => {
            const sb = sortBy || 'createdAt';
            const so = sortOrder || 'desc';
            await scopeDocumentIntegration?.fetchAll(page, pageSize, search, sb, so, {});
            return { success: true };
        },
    });

    wireIntegrationLocate({
        integration: assessmentIntegration,
        useStore: useAssessmentStore,
        getItems: (s) => s.assessments || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                assessmentIntegration?.fetchAssessments({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: assessmentQuestionIntegration,
        useStore: useAssessmentStore,
        getItems: (s) => s.assessmentQuestions,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                assessmentQuestionIntegration?.fetchAssessmentQuestions({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortDirection: sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: competenceIntegration,
        useStore: useCompetenceStore,
        getItems: (s) => s.competences || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                competenceIntegration?.fetchCompetences(page, pageSize, search, sortBy, sortOrder, extraQuery),
            ),
    });

    wireIntegrationLocate({
        integration: objectiveIntegration,
        useStore: useObjectiveStore,
        getItems: (s) => s.objectives || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                objectiveIntegration?.fetchObjectives(page, pageSize, search, sortBy, sortOrder, extraQuery),
            ),
    });

    wireIntegrationLocate({
        integration: vulnerabilityIntegration,
        useStore: useVulnerabilityStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                vulnerabilityIntegration?.fetchAll(page, pageSize, search, sortBy, sortOrder, extraQuery),
            ),
    });

    wireIntegrationLocate({
        integration: trainingIntegration,
        useStore: useTrainingStore,
        getItems: (s) => s.trainings || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                trainingIntegration?.fetchTrainings(page, pageSize, search, sortBy, sortOrder, extraQuery),
            ),
    });

    wireIntegrationLocate({
        integration: changeRequestIntegration,
        useStore: useChangeRequestStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                changeRequestIntegration?.fetchAll(page, pageSize, search, sortBy, sortOrder, extraQuery),
            ),
    });

    wireIntegrationLocate({
        integration: problemIntegration,
        useStore: useProblemStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                problemIntegration?.fetchAll(page, pageSize, search, sortBy, sortOrder, extraQuery),
            ),
    });

    wireIntegrationLocate({
        integration: serviceCatalogItemIntegration,
        useStore: useServiceCatalogItemStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
         
        fetchList: async (page, pageSize, search = '', sortBy = '', sortOrder = 'desc', _cf = {}) => {
            const sb = sortBy || 'createdAt';
            const so = sortOrder || 'desc';
            await serviceCatalogItemIntegration?.fetchAll(page, pageSize, search, sb, so, {});
            return { success: true };
        },
    });

    wireIntegrationLocate({
        integration: userIntegration,
        useStore: useUserStore,
        getItems: (s) => s.users || s.items,
        getRowId: (row) => String(row?.id ?? row?._id ?? ''),
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                userIntegration?.fetchUsers(page, pageSize, search, sortBy, sortOrder, extraQuery),
            ),
    });

    wireIntegrationLocate({
        integration: roleIntegration,
        useStore: useRoleStore,
        getItems: (s) => s.roles || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                roleIntegration?.fetchRoles({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                    applySystemRoleVisibility: true,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: delegationIntegration,
        useStore: useDelegationStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                delegationIntegration?.fetchAll({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: assetGroupIntegration,
        useStore: useAssetGroupStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                assetGroupIntegration?.fetchAssetGroups({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: assetCategoryIntegration,
        useStore: useAssetCategoryStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                assetCategoryIntegration?.fetchAssetCategories?.({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }) ??
                assetCategoryIntegration?.fetchAll?.({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: pdInventoryIntegration,
        useStore: usePdInventoryStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                pdInventoryIntegration?.fetchPdInventoryList?.({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }) ??
                pdInventoryIntegration?.fetchAll?.({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: workflowTemplateIntegration,
        useStore: useWorkflowTemplateStore,
        getItems: (s) => s.templates || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                workflowTemplateIntegration?.fetchTemplates({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: partyTemplateIntegration,
        useStore: usePartyTemplateStore,
        getItems: (s) => s.templates || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                partyTemplateIntegration?.fetchTemplates({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId: tenantId(),
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: webhookSubscriptionIntegration,
        useStore: useWebhookSubscriptionStore,
        getItems: (s) => s.items || s.subscriptions,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                webhookSubscriptionIntegration?.fetchAll?.({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: ticketIntegration,
        useStore: useTicketStore,
        getItems: (s) => s.tickets,
        service: ticketApi,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                ticketIntegration?.fetchTickets({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: correctiveActionIntegration,
        useStore: useCorrectiveActionStore,
        getItems: (s) => s.correctiveActions || s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                correctiveActionIntegration?.fetchCorrectiveActions(
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    extraQuery,
                ),
            ),
    });

    wireIntegrationLocate({
        integration: auditEvaluationIntegration,
        useStore: useAuditEvaluationStore,
        getItems: (s) => s.myAudits,
        service: auditEvaluationIntegration?.auditAssignment,
        defaultSortBy: 'title',
        defaultSortOrder: 'asc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder) =>
                auditEvaluationIntegration?.fetchMyAudits({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortDirection: sortOrder,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: assignmentTemplateIntegration,
        useStore: useAssignmentTemplateStore,
        getItems: (s) => s.items,
        service: assignmentTemplateApi,
        defaultSortBy: 'name',
        defaultSortOrder: 'asc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
                assignmentTemplateIntegration?.fetchAll({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    ...extraQuery,
                }),
            ),
    });

    wireIntegrationLocate({
        integration: weaknessIntegration,
        useStore: useWeaknessStore,
        getItems: (s) => s.weaknesses,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            weaknessIntegration?.fetchWeaknesses(page, pageSize, search, sortBy, sortOrder, {}, extraQuery),
        ),
    });

    wireIntegrationLocate({
        integration: formTemplateIntegration,
        useStore: useSystemSurveysStore,
        getItems: (s) => s.surveys,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, columnFilters) =>
            formTemplateIntegration?.fetchSurveys(page, pageSize, search, sortBy, sortOrder, columnFilters),
        ),
    });

    wireIntegrationLocate({
        integration: dpiaAssessmentIntegration,
        useStore: useDpiaAssessmentStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder) =>
            dpiaAssessmentIntegration?.fetchItems({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
            }),
        ),
    });

    wireIntegrationLocate({
        integration: privacyNoticeIntegration,
        useStore: usePrivacyNoticeStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            privacyNoticeIntegration?.fetchItems({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                ...extraQuery,
            }),
        ),
    });

    wireIntegrationLocate({
        integration: dataBreachIntegration,
        useStore: useDataBreachStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            dataBreachIntegration?.fetchItems({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                ...extraQuery,
            }),
        ),
    });

    wireIntegrationLocate({
        integration: dataSubjectRequestIntegration,
        useStore: useDataSubjectRequestStore,
        getItems: (s) => s.items,
        defaultSortBy: 'createdAt',
        defaultSortOrder: 'desc',
        fetchList: descList((page, pageSize, search, sortBy, sortOrder, extraQuery) =>
            dataSubjectRequestIntegration?.fetchItems({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                ...extraQuery,
            }),
        ),
    });
}
